/**
 * Opening the specific thing a notification is about, after switching tabs.
 *
 * One path for both ways in — tapping a notification in the app, and tapping
 * a push — which had drifted: the push path never opened an expense or a
 * memory, and ignored the old tab names stored on older notifications.
 *
 * Expenses and Memories load on first visit, so the tab may not exist yet when
 * the event goes out. The request is kept for a few seconds and the tab takes
 * it when it mounts (takePendingNav), so a first-visit tap isn't lost.
 */

export type NavEventName = 'nav-expense-view' | 'nav-memory-view' | 'nav-itinerary-day' | 'nav-settle-section' | 'nav-itinerary-gaps';
type NavEvent = { name: NavEventName; detail: string };
export type DeepLink = { tab?: string; itemId?: string; section?: string };

/** Old tab names on notifications stored before the nav restructure. */
export const resolveTab = (tab?: string) => tab === 'trips' ? 'logistics' : tab === 'itinerary' ? 'bucketlist' : tab;

export function deepLinkEvent(link: DeepLink): NavEvent | null {
  const tab = resolveTab(link.tab);
  if (tab === 'expenses' && (link.section === 'settled-received' || link.section === 'settled-paid')) {
    return { name: 'nav-settle-section', detail: link.section };
  }
  if (tab === 'logistics' && link.section === 'gaps') return { name: 'nav-itinerary-gaps', detail: 'gaps' };
  if (!link.itemId) return null;
  if (tab === 'expenses') return { name: 'nav-expense-view', detail: link.itemId };
  if (tab === 'logistics' || tab === 'bucketlist') return { name: 'nav-itinerary-day', detail: `itinerary-${link.itemId}` };
  if (tab === 'memories') return { name: 'nav-memory-view', detail: link.itemId };
  return null;
}

let pending: (NavEvent & { at: number }) | null = null;

/** Call after switching to the link's tab. */
export function openDeepLink(link: DeepLink, delayMs = 150): void {
  const event = deepLinkEvent(link);
  if (!event) return;
  const request = { ...event, at: Date.now() };
  pending = request;
  setTimeout(() => {
    if (pending === request) window.dispatchEvent(new CustomEvent(event.name, { detail: event.detail }));
  }, delayMs);
}

/** A tab calls this when it handles `name` (so it isn't replayed) and on mount (to catch one sent before it existed). */
export function takePendingNav(name: NavEventName): string | null {
  const detail = peekPendingNav(name);
  if (!detail) return null;
  pending = null;
  return detail;
}

/** Data-backed tabs can wait for their destination before consuming the link. */
export function peekPendingNav(name: NavEventName): string | null {
  return pending?.name === name && Date.now() - pending.at < 5000 ? pending.detail : null;
}
